<!-- components/dashboard/WorldClocks.vue -->
<template>
  <el-card shadow="always" class="world-clock-card">
    <template #header>
      <span class="title">🌐 世界时钟</span>
    </template>
    
    <el-row :gutter="20" class="clocks-grid">
      <el-col 
        v-for="clock in clocks" 
        :key="clock.timezone" 
        :span="6" 
        class="clock-item"
      >
        <div class="clock">
          <div class="header">
            <span class="flag">{{ clock.flag_emoji }}</span>
            <span class="city">{{ clock.city_name_zh }}</span>
          </div>
          <div class="time">{{ clock.time }}</div>
          <div class="date">
            {{ clock.date }} 
            <span class="weekday">{{ clock.weekday }}</span>
          </div>
        </div>
      </el-col>
    </el-row>
  </el-card>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import request from '@/utils/axios'

const displayOrder = [
  'America/New_York',
  'America/Los_Angeles',
  'Europe/London',
  'Australia/Sydney',
  'America/Sao_Paulo',
  'Africa/Lagos',
  'Africa/Tripoli',
  'Indian/Mauritius'
]

const clocks = ref([])

const formatClockTime = (timezone) => {
  const now = new Date()
  const formatterTime = new Intl.DateTimeFormat('zh-CN', {
    timeZone: timezone,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  })
  const formatterDate = new Intl.DateTimeFormat('zh-CN', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  })
  const formatterWeekday = new Intl.DateTimeFormat('zh-CN', {
    timeZone: timezone,
    weekday: 'short'
  })

  return {
    time: formatterTime.format(now),
    date: formatterDate.format(now).replace(/\//g, '-'),
    weekday: formatterWeekday.format(now)
  }
}

const loadClocks = async () => {
  try {
    const res = await request.get('/dashboard/overview/')
    const data = res?.data || res || {}
    let rawClocks = data.world_clocks || []

    const cityMap = {
      'America/New_York': '美东',
      'America/Los_Angeles': '美西',
      'Europe/London': '伦敦',
      'Australia/Sydney': '澳大利亚',
      'America/Sao_Paulo': '巴西',
      'Africa/Lagos': '尼日利亚',
      'Africa/Tripoli': '利比亚',
      'Indian/Mauritius': '毛里求斯'
    }

    rawClocks = rawClocks.map(clock => {
      const { time, date, weekday } = formatClockTime(clock.timezone)
      return {
        ...clock,
        city_name_zh: cityMap[clock.timezone] || clock.city_name,
        time,
        date,
        weekday
      }
    })

    // 严格按指定顺序
    const ordered = displayOrder
      .map(tz => rawClocks.find(c => c.timezone === tz))
      .filter(Boolean)

    clocks.value = ordered
  } catch (error) {
    console.error('[WorldClocks] 加载失败:', error)
  }
}

let timer = null
const updateAllTimes = () => {
  clocks.value.forEach(clock => {
    const { time, date, weekday } = formatClockTime(clock.timezone)
    clock.time = time
    clock.date = date
    clock.weekday = weekday
  })
}

onMounted(() => {
  loadClocks()
  timer = setInterval(updateAllTimes, 10000) // 每10秒刷新
})

onUnmounted(() => {
  if (timer) clearInterval(timer)
})
</script>

<style scoped>
.world-clock-card {
  height: 100%;
  border: 1px solid #409eff;
  box-shadow: 0 4px 20px rgba(64, 158, 255, 0.15);
  transition: all 0.3s ease;
}

.world-clock-card:hover {
  box-shadow: 0 8px 30px rgba(64, 158, 255, 0.25);
}

.title {
  font-size: 20px;
  font-weight: 700;
  color: #303133;
  letter-spacing: 2px;
  text-shadow: 0 0 8px rgba(64, 158, 255, 0.5);
}

.clocks-grid {
  padding: 8px 4px;
}

.clock {
  text-align: center;
  padding: 24px 12px;
  border-radius: 12px;
  transition: all 0.3s ease;
  background: rgba(255,255,255,0.6);
}

.clock:hover {
  background: rgba(64, 158, 255, 0.08);
  transform: translateY(-4px);
}

.header {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
  margin-bottom: 18px;
}

.flag {
  font-size: 42px;
  line-height: 1;
}

.city {
  font-size: 22px;
  font-weight: 700;
  color: #303133;
  letter-spacing: 1.5px;
}

.time {
  font-size: 40px;
  font-weight: 700;
  letter-spacing: 8px;
  color: #409eff;
  font-family: 'Courier New', 'Lucida Console', monospace;
  text-shadow: 0 0 15px rgba(64, 158, 255, 0.7);
  margin: 8px 0 16px;
}

.date {
  font-size: 16px;
  color: #606266;
  font-family: 'Courier New', Courier, monospace;
}

.weekday {
  margin-left: 12px;
  color: #67c23a;
  font-weight: 700;
}

.clock-item:hover .time {
  text-shadow: 0 0 22px rgba(64, 158, 255, 0.9);
}
</style>